import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Ticket, ArrowRight, CheckCircle2, XCircle, Lightbulb, ExternalLink,
  BookOpen, ShieldCheck, Send, Bot, ChevronLeft, Trophy, Target, ListChecks, Search, X,
} from "lucide-react";

function renderRich(text) {
  return String(text).split(/(\*\*.+?\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>);
}

const DIFF = {
  Beginner: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  Intermediate: "bg-amber-500/10 text-amber-600 border-amber-500/30",
  Advanced: "bg-orange-500/10 text-orange-600 border-orange-500/30",
  Expert: "bg-rose-500/10 text-rose-600 border-rose-500/30",
  Engineer: "bg-rose-500/10 text-rose-600 border-rose-500/30",
};
const TRACK_ACCENT = {
  foundations: "#10b981", core: "#0078d4", advanced: "#f59e0b", expert: "#e11d48",
};
const PRIO = { Critical: "text-rose-600", High: "text-amber-600", Medium: "text-blue-600", Low: "text-muted-foreground" };

function Mentor({ scenarioId, taskId }) {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  const ask = async () => {
    if (!q.trim() || busy) return;
    const question = q.trim();
    const history = msgs.map((m) => ({ role: m.role, text: m.text }));
    setMsgs((p) => [...p, { role: "student", text: question }]);
    setQ(""); setBusy(true);
    try {
      const { data } = await api.post("/guided/mentor", { scenario_id: scenarioId, task_id: taskId, question, history });
      setMsgs((p) => [...p, { role: "mentor", text: data.reply }]);
    } catch (e) {
      setMsgs((p) => [...p, { role: "mentor", text: "I couldn't respond right now — try again in a moment." }]);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed bottom-5 right-5 z-40" data-testid="mentor-widget">
      {open && (
        <div className="mb-3 w-80 sm:w-96 rounded-2xl border border-border bg-card shadow-2xl overflow-hidden flex flex-col" style={{ height: 440 }}>
          <div className="flex items-center gap-2 px-4 py-3 bg-[#0078d4] text-white">
            <Bot className="h-5 w-5" /> <span className="font-semibold text-sm">HMG Cloud Mentor</span>
            <button className="ml-auto text-white/80 hover:text-white" onClick={() => setOpen(false)}>✕</button>
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2.5 text-sm">
            {msgs.length === 0 && (
              <div className="text-muted-foreground text-xs bg-muted/50 rounded-lg p-3">
                Stuck? Ask me things like “Where should I investigate?” or “Why do I need an NSG?”. I’ll guide you — I won’t just hand over the answer.
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.role === "student" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${m.role === "student" ? "bg-[#0078d4] text-white" : "bg-muted"}`} data-testid={`mentor-msg-${m.role}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs text-muted-foreground">Mentor is thinking…</div>}
            <div ref={endRef} />
          </div>
          <div className="p-2 border-t border-border flex gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Ask the mentor…" data-testid="mentor-input"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm" />
            <Button size="icon" className="bg-[#0078d4] hover:bg-[#106ebe]" onClick={ask} disabled={busy} data-testid="mentor-send"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <Button onClick={() => setOpen((o) => !o)} data-testid="mentor-toggle"
        className="rounded-full h-14 w-14 shadow-xl bg-gradient-to-br from-[#0078d4] to-[#50e6ff] text-white">
        <Bot className="h-6 w-6" />
      </Button>
    </div>
  );
}

function ScenarioRunner({ scenarioId, onBack }) {
  const [scn, setScn] = useState(null);
  const [prog, setProg] = useState(null);
  const [result, setResult] = useState(null);
  const [validating, setValidating] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.post(`/guided/scenarios/${scenarioId}/start`);
    setScn(data.scenario); setProg(data.progress);
  }, [scenarioId]);
  useEffect(() => { load(); }, [load]);

  if (!scn) return <div className="p-8 text-muted-foreground">Loading scenario…</div>;

  const taskStatus = (tid) => prog?.tasks?.[tid]?.status || "locked";
  const activeIdx = scn.tasks.findIndex((t) => taskStatus(t.id) === "active");
  const curIdx = activeIdx >= 0 ? activeIdx : scn.tasks.length;
  const task = scn.tasks[curIdx] || null;
  const completed = prog?.status === "completed";

  const validate = async () => {
    if (!task) return;
    setValidating(true); setResult(null);
    try {
      const { data } = await api.post(`/guided/scenarios/${scenarioId}/tasks/${task.id}/validate`);
      setResult(data);
      const r = await api.get(`/guided/scenarios/${scenarioId}`);
      setProg(r.data.progress);
      if (data.passed) toast.success("Task validated");
    } catch (e) { toast.error(apiErr(e)); } finally { setValidating(false); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="scenario-runner">
      <Button variant="ghost" onClick={onBack} className="gap-2 text-muted-foreground -ml-2 mb-3"><ChevronLeft className="h-4 w-4" /> All labs</Button>

      {/* Ticket header */}
      <div className="rounded-2xl border border-[#0078d4]/25 bg-gradient-to-br from-[#0078d4]/10 to-transparent p-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${DIFF[scn.difficulty]}`}>{scn.difficulty} mode</span>
          <span className="flex items-center gap-1.5 text-sm font-mono"><Ticket className="h-4 w-4 text-[#0078d4]" /> {scn.ticket}</span>
          <span className={`text-sm font-semibold ${PRIO[scn.priority]}`}>{scn.priority} priority</span>
          <span className="text-xs text-muted-foreground">{scn.environment}</span>
        </div>
        <h1 className="font-display font-extrabold text-2xl mt-3 tracking-tight">{scn.title}</h1>
        <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{scn.business}</p>
        <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
          <span><strong className="text-foreground">Your role:</strong> {scn.role}</span>
          <span><strong className="text-foreground">Requested by:</strong> {scn.requested_by}</span>
        </div>
      </div>

      {/* Architecture */}
      <div className="mt-5 rounded-xl border border-border bg-card p-5">
        <div className="flex items-center gap-2 font-semibold text-sm"><BookOpen className="h-4 w-4 text-[#0078d4]" /> Architecture overview</div>
        <div className="mt-3 space-y-2">
          {scn.architecture.map((a, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <span className="font-mono text-[#0078d4] shrink-0">{a.c}</span>
              <span className="text-muted-foreground">— {a.d}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Task tracker */}
      <div className="mt-5 flex items-center gap-2 flex-wrap">
        {scn.tasks.map((t, i) => {
          const st = taskStatus(t.id);
          return (
            <div key={t.id} data-testid={`task-pill-${t.id}`} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
              st === "passed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30"
              : st === "active" ? "bg-[#0078d4]/10 text-[#0078d4] border-[#0078d4]/30"
              : "bg-muted text-muted-foreground border-border"}`}>
              {st === "passed" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="font-mono">{i + 1}</span>}
              {t.title}
            </div>
          );
        })}
      </div>

      {/* Completion */}
      {completed && (
        <div className="mt-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-6 text-center" data-testid="scenario-complete">
          <Trophy className="h-8 w-8 text-emerald-500 mx-auto" />
          <h2 className="font-display font-bold text-xl mt-2">Scenario complete — ticket resolved!</h2>
          <div className="mt-3 text-sm text-muted-foreground">What you learned:</div>
          <ul className="mt-1 text-sm inline-block text-left">
            {scn.outcomes.map((o, i) => <li key={i} className="flex items-center gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> {o}</li>)}
          </ul>
          <div><Button onClick={onBack} className="mt-4 bg-[#0078d4] hover:bg-[#106ebe]">Back to labs <ArrowRight className="h-4 w-4 ml-1" /></Button></div>
        </div>
      )}

      {/* Current task */}
      {!completed && task && (
        <div className="mt-6 rounded-xl border border-border bg-card p-6" data-testid="current-task">
          <div className="text-xs text-muted-foreground font-medium">Task {curIdx + 1} of {scn.tasks.length}</div>
          <h2 className="font-display font-bold text-lg mt-1">{task.title}</h2>
          <p className="text-sm mt-2">{task.instruction}</p>
          <div className="mt-3 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Why this matters: </span>{task.why}
          </div>

          {(task.steps || []).length > 0 && (
            <div className="mt-4 rounded-lg border border-[#0078d4]/25 bg-[#0078d4]/5 p-4" data-testid="guided-steps">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#0078d4] mb-2.5">
                <ListChecks className="h-4 w-4" /> Steps to complete
              </div>
              <ol className="space-y-2">
                {task.steps.map((s, i) => (
                  <li key={i} className="flex gap-2.5 text-sm leading-relaxed" data-testid={`guided-step-${i}`}>
                    <span className="shrink-0 h-5 w-5 rounded-full bg-[#0078d4] text-white text-[11px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                    <span>{renderRich(s)}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <a href="/azure" target="_blank" rel="noreferrer">
              <Button variant="outline" className="gap-2" data-testid="open-portal"><ExternalLink className="h-4 w-4" /> Open Azure Portal</Button>
            </a>
            <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={validate} disabled={validating} data-testid="validate-work">
              <ShieldCheck className="h-4 w-4" /> {validating ? "Checking…" : "Validate my work"}
            </Button>
          </div>

          {result && (
            <div className={`mt-4 rounded-lg border p-4 ${result.passed ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/5"}`} data-testid="validate-result">
              <div className="flex items-center gap-2 font-semibold">
                {result.passed ? <CheckCircle2 className="h-5 w-5 text-emerald-500" /> : <XCircle className="h-5 w-5 text-rose-500" />}
                {result.passed ? "Task completed" : "Validation failed"}
              </div>
              <p className="text-sm mt-1">{result.message}</p>
              {(result.expected || result.current) && (
                <div className="mt-2 text-xs font-mono grid grid-cols-2 gap-2 max-w-sm">
                  {result.expected && <div className="rounded bg-emerald-500/10 px-2 py-1">Expected: {result.expected}</div>}
                  {result.current && <div className="rounded bg-rose-500/10 px-2 py-1">Current: {result.current}</div>}
                </div>
              )}
              {result.hint && (
                <div className="mt-3 flex items-start gap-2 text-sm bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-lg p-3" data-testid="task-hint">
                  <Lightbulb className="h-4 w-4 mt-0.5 shrink-0" />
                  <span><strong>Hint {result.hint_level}:</strong> {result.hint}</span>
                </div>
              )}
              {result.passed && result.next_task && (
                <Button size="sm" className="mt-3 gap-1.5 bg-[#0078d4] hover:bg-[#106ebe]" onClick={() => setResult(null)} data-testid="next-task">
                  Next task <ArrowRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      <Mentor scenarioId={scenarioId} taskId={task?.id} />
    </div>
  );
}

export default function GuidedLabs() {
  const [scenarios, setScenarios] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dash, setDash] = useState(null);
  const [cert, setCert] = useState(null);
  const [showCert, setShowCert] = useState(false);
  const [openTrack, setOpenTrack] = useState("foundations");
  const [query, setQuery] = useState("");
  const [skill, setSkill] = useState("");
  const openInPortal = (id) => window.open(`/azure?guided=${id}`, "_blank", "noopener,noreferrer");

  const load = useCallback(async () => {
    const [s, d, c] = await Promise.all([api.get("/guided/scenarios"), api.get("/guided/dashboard"), api.get("/guided/certificate")]);
    setScenarios(s.data.scenarios); setTracks(s.data.tracks || []); setProgress(s.data.progress || null);
    setDash(d.data); setCert(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const trackProg = (key) => (progress?.tracks || []).find((t) => t.key === key) || { pct: 0, completed: 0, total: 0, earned: false };

  const allSkills = Array.from(new Set(scenarios.flatMap((s) => s.skills))).sort();
  const filtering = query.trim() || skill;
  const q = query.trim().toLowerCase();
  const filtered = scenarios.filter((s) =>
    (!q || s.title.toLowerCase().includes(q) || (s.business || "").toLowerCase().includes(q) || s.skills.some((sk) => sk.toLowerCase().includes(q)))
    && (!skill || s.skills.includes(skill)));

  const LabCard = (s) => (
    <button key={s.id} onClick={() => openInPortal(s.id)} data-testid={`scenario-card-${s.id}`}
      className="w-full text-left rounded-lg border border-border bg-background p-4 hover:border-[#0078d4]/50 transition-colors">
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${DIFF[s.difficulty] || DIFF.Intermediate}`}>{s.difficulty}</span>
        {s.est_min && <span className="text-[11px] text-muted-foreground">~{s.est_min} min</span>}
        {s.status === "completed" && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Completed</span>}
        {s.status === "in_progress" && <span className="text-xs text-amber-600 font-medium">In progress · {s.completed_tasks}/{s.task_count}</span>}
        <ArrowRight className="h-4 w-4 text-muted-foreground ml-auto" />
      </div>
      <h3 className="font-semibold text-base mt-1.5">{s.title}</h3>
      <p className="text-sm text-muted-foreground mt-0.5 line-clamp-2">{s.business}</p>
      <div className="mt-2 flex gap-1.5 flex-wrap">
        {s.skills.map((sk) => <span key={sk} className="text-[10px] uppercase tracking-wide bg-muted px-2 py-0.5 rounded">{sk}</span>)}
      </div>
    </button>
  );

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="guided-labs">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-[#0078d4] grid place-items-center text-white"><GraduationCap className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display font-extrabold text-2xl tracking-tight leading-none">Azure Cloud Academy</h1>
          <p className="text-sm text-muted-foreground mt-1">{scenarios.length} hands-on labs · zero to expert. Every lab is validated against real Azure state — no clicking Next.</p>
        </div>
      </div>

      {/* Track progress overview */}
      {progress && (
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="academy-track-overview">
          {tracks.map((t) => {
            const tp = trackProg(t.key);
            const accent = TRACK_ACCENT[t.key];
            return (
              <div key={t.key} className="rounded-xl border border-border bg-card p-4" data-testid={`track-card-${t.key}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: accent }}>{t.name}</span>
                  {tp.earned && <Trophy className="h-4 w-4 text-amber-500" />}
                </div>
                <div className="mt-2 text-2xl font-bold">{tp.completed}<span className="text-sm text-muted-foreground">/{tp.total}</span></div>
                <div className="h-1.5 rounded-full bg-muted mt-2 overflow-hidden"><div className="h-full" style={{ width: `${tp.pct}%`, background: accent }} /></div>
                <div className="text-[11px] text-muted-foreground mt-1.5">{t.tagline}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dashboard strip */}
      {dash && (
        <div className="mt-5 rounded-xl border border-border bg-card p-5" data-testid="guided-dashboard">
          <div className="flex flex-wrap items-center gap-6">
            <div><div className="text-xs text-muted-foreground">Role</div><div className="font-semibold">{dash.role}</div></div>
            <div><div className="text-xs text-muted-foreground">Level</div><div className="font-semibold">{dash.level}</div></div>
            <div><div className="text-xs text-muted-foreground">Tickets resolved</div><div className="font-semibold">{dash.tickets_resolved}</div></div>
            <div><div className="text-xs text-muted-foreground">Incidents resolved</div><div className="font-semibold">{dash.incidents_resolved}</div></div>
            <div className="flex items-center gap-2"><Target className="h-4 w-4 text-[#0078d4]" /><div><div className="text-xs text-muted-foreground">Overall skill</div><div className="font-semibold">{dash.overall_skill}%</div></div></div>
            {dash.next_recommended && (
              <div className="ml-auto text-right"><div className="text-xs text-muted-foreground">Recommended next</div>
                <button className="text-sm text-[#0078d4] font-medium hover:underline" onClick={() => openInPortal(dash.next_recommended.id)}>{dash.next_recommended.title} →</button></div>
            )}
          </div>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            {dash.skills.map((sk) => (
              <div key={sk.skill}>
                <div className="flex justify-between text-xs"><span>{sk.skill}</span><span className="font-mono text-muted-foreground">{sk.pct}%</span></div>
                <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full bg-[#0078d4]" style={{ width: `${sk.pct}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Certificate progress banner */}
      {cert && (
        <div className="mt-5 rounded-xl border border-[#0078d4]/30 bg-[#0078d4]/5 p-5 flex flex-wrap items-center gap-4" data-testid="guided-cert-banner">
          <Trophy className={`h-8 w-8 ${cert.eligible ? "text-amber-500" : "text-muted-foreground"}`} />
          <div className="min-w-0">
            <div className="font-semibold">Azure Cloud Engineer Certificate</div>
            <div className="text-sm text-muted-foreground">{cert.completed}/{cert.total} labs ({cert.overall_pct}%){cert.eligible ? " — certificate unlocked!" : " — finish Foundations + Core and reach 80% to unlock."}</div>
            {cert.track_badges?.length > 0 && (
              <div className="mt-1.5 flex gap-1.5 flex-wrap">
                {cert.track_badges.map((b) => <span key={b} className="text-[10px] uppercase tracking-wide bg-amber-500/15 text-amber-600 px-2 py-0.5 rounded-full flex items-center gap-1"><Trophy className="h-3 w-3" />{b}</span>)}
              </div>
            )}
            <div className="h-1.5 rounded-full bg-muted mt-2 w-64 overflow-hidden"><div className="h-full bg-[#0078d4]" style={{ width: `${cert.overall_pct || 0}%` }} /></div>
          </div>
          <button disabled={!cert.eligible} onClick={() => setShowCert(true)} data-testid="guided-cert-view"
            className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium ${cert.eligible ? "bg-[#0078d4] text-white hover:bg-[#106ebe]" : "bg-muted text-muted-foreground cursor-not-allowed"}`}>
            {cert.eligible ? "View certificate" : "Locked"}
          </button>
        </div>
      )}

      {showCert && cert?.certificate && <CertificateModal cert={cert.certificate} onClose={() => setShowCert(false)} />}

      {/* Search & skill filter */}
      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="academy-search-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="academy-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search 100+ labs by name, skill or scenario…"
            className="w-full pl-9 pr-9 py-2.5 rounded-lg border border-input bg-background text-sm" />
          {query && <button onClick={() => setQuery("")} data-testid="academy-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
        </div>
        <select data-testid="academy-skill-filter" value={skill} onChange={(e) => setSkill(e.target.value)}
          className="rounded-lg border border-input bg-background px-3 py-2.5 text-sm">
          <option value="">All skills</option>
          {allSkills.map((sk) => <option key={sk} value={sk}>{sk}</option>)}
        </select>
      </div>

      {filtering ? (
        <div className="mt-4" data-testid="academy-search-results">
          <div className="text-sm text-muted-foreground mb-2">{filtered.length} lab{filtered.length === 1 ? "" : "s"} match{query && ` "${query}"`}{skill && ` · ${skill}`}</div>
          <div className="space-y-2">
            {filtered.length === 0 ? <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">No labs match your search.</div> : filtered.map((s) => LabCard(s))}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          {tracks.map((t) => {
            const labs = scenarios.filter((s) => s.track === t.key);
            if (labs.length === 0) return null;
            const tp = trackProg(t.key);
            const accent = TRACK_ACCENT[t.key];
            const isOpen = openTrack === t.key;
            return (
              <div key={t.key} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`track-section-${t.key}`}>
                <button className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-muted/40 transition-colors"
                  onClick={() => setOpenTrack(isOpen ? null : t.key)} data-testid={`track-toggle-${t.key}`}>
                  <span className="h-9 w-9 rounded-lg grid place-items-center text-white font-bold" style={{ background: accent }}>{t.order}</span>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-lg flex items-center gap-2">{t.name}{tp.earned && <Trophy className="h-4 w-4 text-amber-500" />}</div>
                    <div className="text-xs text-muted-foreground">{tp.completed}/{tp.total} labs · {t.tagline}</div>
                  </div>
                  <ChevronLeft className={`h-5 w-5 text-muted-foreground ml-auto transition-transform ${isOpen ? "-rotate-90" : ""}`} />
                </button>
                {isOpen && <div className="px-3 pb-3 space-y-2">{labs.map((s) => LabCard(s))}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function CertificateModal({ cert, onClose }) {
  const print = () => window.print();
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="guided-cert-modal">
      <div className="bg-white text-slate-900 rounded-xl max-w-2xl w-full p-0 overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div id="cert-print" className="p-10 border-[6px] border-double border-[#0078d4] m-4 text-center">
          <div className="text-[11px] tracking-[0.3em] text-[#0078d4] font-semibold uppercase">{cert.issuer}</div>
          <div className="mt-6 text-3xl font-extrabold tracking-tight">Certificate of Completion</div>
          <div className="mt-2 text-sm text-slate-500">This certifies that</div>
          <div className="mt-3 text-2xl font-bold text-[#0078d4]">{cert.student_name}</div>
          <div className="mt-3 text-sm text-slate-600 max-w-md mx-auto">has successfully completed the <strong>{cert.title}</strong>, demonstrating hands-on proficiency across the Azure Cloud Academy{cert.labs_completed ? ` (${cert.labs_completed} labs)` : ""}.</div>
          {cert.track_badges?.length > 0 && (
            <div className="mt-3 flex flex-wrap justify-center gap-1.5">
              {cert.track_badges.map((b) => <span key={b} className="text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">🏅 {b}</span>)}
            </div>
          )}
          {cert.distinction && <div className="mt-2 text-xs font-semibold text-amber-600">{cert.distinction}</div>}
          <div className="mt-5 flex flex-wrap justify-center gap-1.5">
            {cert.skills.map((s) => <span key={s} className="text-[10px] uppercase tracking-wide bg-[#0078d4]/10 text-[#0078d4] px-2 py-0.5 rounded-full">{s}</span>)}
          </div>
          <div className="mt-6 flex justify-between text-xs text-slate-500 px-4">
            <div>Issued: <strong className="text-slate-700">{cert.issued_date}</strong></div>
            <div>Score: <strong className="text-slate-700">{cert.score}%</strong></div>
            <div>ID: <strong className="text-slate-700">{cert.certificate_id}</strong></div>
          </div>
          <div className="mt-5 text-[9px] text-slate-400 max-w-lg mx-auto">{cert.disclaimer}</div>
        </div>
        <div className="flex gap-2 justify-end p-4 border-t print:hidden">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button className="bg-[#0078d4] text-white" onClick={print} data-testid="guided-cert-print">Print / Save PDF</Button>
        </div>
      </div>
    </div>
  );
}
